import { PublicEventStatusDto, SourcingEventStatus } from "../../autogen/bff-api";

export type BucketKey =
  | "arbeid"
  | "kunngjort"
  | "evaluering"
  | "tildeling"
  | "fullfort"
  | "avlyst";

export type BucketBadgeKey =
  | "anskArbeid"
  | "anskKunngjort"
  | "anskEvaluering"
  | "anskTildeling"
  | "anskFullfort"
  | "anskAvlyst";

export const STATUS_BUCKET: Record<SourcingEventStatus, BucketKey> = {
  [SourcingEventStatus.Draft]: "arbeid",
  [SourcingEventStatus.PublicationPending]: "arbeid",
  [SourcingEventStatus.PublicationFailed]: "arbeid",
  [SourcingEventStatus.Published]: "kunngjort",
  [SourcingEventStatus.Evaluation]: "evaluering",
  [SourcingEventStatus.Evaluated]: "evaluering",
  [SourcingEventStatus.AwardDecision]: "tildeling",
  [SourcingEventStatus.Awarded]: "tildeling",
  [SourcingEventStatus.Closed]: "fullfort",
  [SourcingEventStatus.Cancelled]: "avlyst",
  [SourcingEventStatus.Completed]: "fullfort",
};

export const BUCKET_META: Record<BucketKey, { labelKey: string; badgeKey: BucketBadgeKey }> = {
  arbeid: { labelKey: "In progress", badgeKey: "anskArbeid" },
  kunngjort: { labelKey: "Announced", badgeKey: "anskKunngjort" },
  evaluering: { labelKey: "Under evaluation", badgeKey: "anskEvaluering" },
  tildeling: { labelKey: "Award and standstill", badgeKey: "anskTildeling" },
  fullfort: { labelKey: "Completed", badgeKey: "anskFullfort" },
  avlyst: { labelKey: "Cancelled_competition", badgeKey: "anskAvlyst" },
};

export const STATUSES_BY_BUCKET: Record<BucketKey, SourcingEventStatus[]> = (
  Object.entries(STATUS_BUCKET) as [SourcingEventStatus, BucketKey][]
).reduce(
  (acc, [status, bucket]) => {
    acc[bucket].push(status);
    return acc;
  },
  Object.fromEntries(
    (Object.keys(BUCKET_META) as BucketKey[]).map((bucket) => [bucket, [] as SourcingEventStatus[]]),
  ) as Record<BucketKey, SourcingEventStatus[]>,
);

export const statusesForBuckets = (buckets: BucketKey[]): SourcingEventStatus[] =>
  buckets.flatMap((bucket) => STATUSES_BY_BUCKET[bucket]);

export const parseStatusBuckets = (raw: string): BucketKey[] => {
  const buckets = raw
    .split(",")
    .map((token) => token.trim())
    .filter(Boolean)
    .map((token) => (isBucketKey(token) ? token : undefined))
    .filter((bucket): bucket is BucketKey => bucket != null);
  return Array.from(new Set(buckets));
};

export interface EventStatusCount {
  publicStatus?: PublicEventStatusDto | null;
  count: number;
}

/**
 * Folds the BFF's per-status counts into badge totals. A status the BFF knows but this build does not
 * is skipped rather than throwing.
 */
export const countBadgesByStatus = (counts: EventStatusCount[]): Partial<Record<BucketBadgeKey, number>> => {
  const badges: Partial<Record<BucketBadgeKey, number>> = {};
  for (const { publicStatus, count } of counts) {
    const bucket = publicStatus ? STATUS_BUCKET[publicStatus as unknown as SourcingEventStatus] : undefined;
    const badgeKey = bucket ? BUCKET_META[bucket]?.badgeKey : undefined;
    if (!badgeKey) continue;
    badges[badgeKey] = (badges[badgeKey] ?? 0) + count;
  }
  return badges;
};

export const MENU_PAGE_PARAM = "status";
export const STATUS_FILTER_PARAM = "statusFilter";

export const bucketQuery = (bucket: BucketKey): string => `${MENU_PAGE_PARAM}=${bucket}`;

export const isBucketKey = (slug: string | null): slug is BucketKey =>
  slug != null && Object.prototype.hasOwnProperty.call(BUCKET_META, slug);
